import { useState } from 'react'
import { DollarSign, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import {
  useValoresDiaria,
  useCriarValorDiaria,
  useAtualizarValorDiaria,
  useExcluirValorDiaria,
} from '../hooks/useAjudaCusto'
import { ValorDiaria } from '../tipos/ajudaCusto'
import { formatarData, formatarMoeda } from '../utils'

interface FormState {
  valor: string
  dataInicioVigencia: string
  dataFimVigencia: string
}

const vazio: FormState = { valor: '', dataInicioVigencia: '', dataFimVigencia: '' }

export default function Parametrizacao() {
  const { data: valores = [], isLoading, refetch, isFetching } = useValoresDiaria()
  const criar = useCriarValorDiaria()
  const atualizar = useAtualizarValorDiaria()
  const excluir = useExcluirValorDiaria()

  const [aberto, setAberto] = useState(false)
  const [editandoId, setEditandoId] = useState<number | null>(null)
  const [form, setForm] = useState<FormState>(vazio)

  const abrirNovo = () => {
    setEditandoId(null)
    setForm(vazio)
    setAberto(true)
  }

  const abrirEdicao = (v: ValorDiaria) => {
    setEditandoId(v.idValorDiariaAjudaCusto ?? null)
    setForm({
      valor: String(v.valor),
      dataInicioVigencia: v.dataInicioVigencia,
      dataFimVigencia: v.dataFimVigencia,
    })
    setAberto(true)
  }

  const salvar = async () => {
    const dto = {
      valor: Number(form.valor),
      dataInicioVigencia: form.dataInicioVigencia,
      dataFimVigencia: form.dataFimVigencia,
    }
    if (editandoId) {
      await atualizar.mutateAsync({ id: editandoId, dto })
    } else {
      await criar.mutateAsync(dto)
    }
    setAberto(false)
  }

  const remover = async (v: ValorDiaria) => {
    if (!v.idValorDiariaAjudaCusto) return
    if (!window.confirm('Excluir esta parametrização?')) return
    await excluir.mutateAsync(v.idValorDiariaAjudaCusto)
  }

  const formValido = Number(form.valor) > 0 && form.dataInicioVigencia && form.dataFimVigencia

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bônus Presencial — Parametrização</h1>
          <p className="text-gray-600">Valor da diária por período de vigência.</p>
        </div>
        <button
          type="button"
          onClick={abrirNovo}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600"
        >
          <Plus className="h-4 w-4" />
          Novo valor
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-theme-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 text-gray-800 font-semibold">
            <DollarSign className="h-5 w-5 text-brand-500" />
            Vigências
          </div>
          <button type="button" onClick={() => refetch()} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Início</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Fim</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {isLoading ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">Carregando...</td></tr>
              ) : valores.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">Nenhuma parametrização cadastrada.</td></tr>
              ) : (
                valores.map((v) => (
                  <tr key={v.idValorDiariaAjudaCusto} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-900 font-medium">{formatarMoeda(v.valor)}</td>
                    <td className="px-4 py-2 text-center text-gray-600">{formatarData(v.dataInicioVigencia)}</td>
                    <td className="px-4 py-2 text-center text-gray-600">{formatarData(v.dataFimVigencia)}</td>
                    <td className="px-4 py-2 text-center">
                      {v.podeEditar ? (
                        <div className="flex items-center justify-center gap-2">
                          <button type="button" onClick={() => abrirEdicao(v)} className="p-1.5 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-md" title="Editar">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button type="button" onClick={() => remover(v)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md" title="Excluir">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400" title="Existem pagamentos vinculados">Bloqueado</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        isOpen={aberto}
        onClose={() => setAberto(false)}
        title={editandoId ? 'Editar valor da diária' : 'Novo valor da diária'}
        icon={<DollarSign className="h-6 w-6 text-brand-500" />}
        size="md"
        footer={
          <div className="flex justify-end gap-3 p-4">
            <button type="button" onClick={() => setAberto(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
            <button
              type="button"
              disabled={!formValido || criar.isLoading || atualizar.isLoading}
              onClick={salvar}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50"
            >
              {editandoId ? 'Atualizar' : 'Salvar'}
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Valor da diária (R$)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.valor}
              onChange={(e) => setForm((f) => ({ ...f, valor: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Início da vigência</label>
              <input type="date" value={form.dataInicioVigencia} onChange={(e) => setForm((f) => ({ ...f, dataInicioVigencia: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Fim da vigência</label>
              <input type="date" value={form.dataFimVigencia} onChange={(e) => setForm((f) => ({ ...f, dataFimVigencia: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>
          </div>
          <p className="text-xs text-gray-400">Somente vigências futuras podem ser cadastradas. Não há sobreposição de períodos.</p>
        </div>
      </Modal>
    </div>
  )
}
